import { tablesDB, DATABASE_ID, USERS_TABLE_ID, Query, ID } from '../lib/appwrite';
import { ADMIN_ROLES } from '../utils/adminPermissions';

const getEndpoint = () =>
  process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://appwrite.beetletz.online/v1';
const getProjectId = () => process.env.REACT_APP_APPWRITE_PROJECT_ID;
const getApiKey = () => process.env.REACT_APP_APPWRITE_API_KEY;

/**
 * Create an Appwrite Auth user (requires API key with users.write scope).
 * Used when adding a new admin so they can log in.
 */
export async function createAuthUser({ userId, email, password, name, phone }) {
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error(
      'API key required to create admin users. Set REACT_APP_APPWRITE_API_KEY in .env with users.write scope.'
    );
  }
  const url = `${getEndpoint()}/users`;
  const headers = {
    'Content-Type': 'application/json',
    'X-Appwrite-Project': getProjectId(),
    'X-Appwrite-Key': apiKey,
  };
  const body = {
    userId: userId || ID.unique(),
    email: email?.trim(),
    password: password || '',
    name: name?.trim() || undefined,
    phone: phone?.trim() || undefined,
  };
  const res = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'omit',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = err.message || err.error || `Failed to create user (${res.status})`;
    if (msg.includes('already exists') || res.status === 409) {
      throw new Error('An account with this email already exists.');
    }
    throw new Error(msg);
  }
  return res.json();
}

/** Platform user roles (customers, sellers, drivers) - used for Users page. Admins excluded. */
export const PLATFORM_ROLES = ['customer', 'seller', 'driver'];

export async function listUsers(queries = []) {
  const res = await tablesDB.listRows(DATABASE_ID, USERS_TABLE_ID, queries);
  return res.rows || [];
}

/**
 * Get platform users for the Users page.
 * Excludes admin users (admin, ceo, manager, marketing, support) - those appear only on Admin Users page.
 */
export async function getPlatformUsers() {
  const res = await tablesDB.listRows(DATABASE_ID, USERS_TABLE_ID, [
    Query.limit(3000),
    Query.orderAsc('fname'),
  ]);
  const rows = (res.rows || []).filter((row) => {
    const roleVal = (row.roles || row.role || '').toLowerCase().trim();
    return !ADMIN_ROLES.includes(roleVal);
  });
  return rows.sort((a, b) => {
    const na = [a.fname, a.lname].filter(Boolean).join(' ').toLowerCase();
    const nb = [b.fname, b.lname].filter(Boolean).join(' ').toLowerCase();
    return na.localeCompare(nb);
  });
}

export async function createUser(data) {
  const rowId = data.$id || ID.unique();
  const record = {
    fname: data.fname || '',
    lname: data.lname || '',
    email: data.email || '',
    phoneNumber: data.phoneNumber || data.phone || '',
    roles: (data.roles || data.role || 'customer').toLowerCase(),
    role: (data.roles || data.role || 'customer').toLowerCase(),
    ...(data.uid != null && { uid: data.uid }),
    ...(data.address != null && { address: data.address }),
  };
  return tablesDB.createRow(DATABASE_ID, USERS_TABLE_ID, rowId, record, null, true);
}

/**
 * Create a new admin user: creates Auth account (so they can log in) and Users table row.
 * Requires REACT_APP_APPWRITE_API_KEY with users.write scope.
 * Credentials are NOT emailed — share the password securely with the new admin.
 */
export async function createAdminUser(data) {
  const userId = ID.unique();
  const name = [data.fname, data.lname].filter(Boolean).join(' ').trim() || data.email;
  const authUser = await createAuthUser({
    userId,
    email: data.email?.trim(),
    password: data.password,
    name: name || undefined,
    phone: data.phoneNumber?.trim() || undefined,
  });
  const rowId = authUser.$id || userId;
  const record = {
    fname: data.fname || '',
    lname: data.lname || '',
    email: data.email || '',
    phoneNumber: data.phoneNumber || data.phone || '',
    roles: (data.roles || data.role || 'support').toLowerCase(),
    role: (data.roles || data.role || 'support').toLowerCase(),
    uid: rowId,
  };
  const row = await tablesDB.createRow(DATABASE_ID, USERS_TABLE_ID, rowId, record, null, true);
  return row;
}

export async function deleteUser(userId) {
  return tablesDB.deleteRow(DATABASE_ID, USERS_TABLE_ID, userId);
}

export async function getUser(userId) {
  return tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, userId);
}

export async function updateUser(userId, data) {
  return tablesDB.updateRow(DATABASE_ID, USERS_TABLE_ID, userId, data);
}

/**
 * Get users who have admin panel access (roles: admin, ceo, manager, marketing, support)
 * Fetches all users and filters by role (column may be `roles` or `role`)
 */
export async function getAdminUsers() {
  const res = await tablesDB.listRows(DATABASE_ID, USERS_TABLE_ID, [
    Query.limit(2000),
    Query.orderAsc('fname'),
  ]);
  const rows = res.rows || [];
  const adminRows = rows.filter((row) => {
    const roleVal = (row.roles || row.role || '').toLowerCase().trim();
    return ADMIN_ROLES.includes(roleVal);
  });
  return adminRows.sort((a, b) => {
    const na = [a.fname, a.lname].filter(Boolean).join(' ').toLowerCase();
    const nb = [b.fname, b.lname].filter(Boolean).join(' ').toLowerCase();
    return na.localeCompare(nb);
  });
}
